/**
 * Shared cover-style helpers. Article/wiki/page covers can carry one
 * of three values: a hex color, a URL, or {@code undefined}. The
 * legacy code originally hardcoded {@code background: cover}, which
 * works for hex (CSS treats it as a color) but produces no visible
 * output for URLs. Centralising the discrimination means every call
 * site renders covers consistently and a future fourth case
 * (gradient string, CSS variable, blurhash, etc.) can be added in
 * one place.
 *
 * <h3>Usage</h3>
 *
 * <pre>{@code
 *   <div style={coverBackground(article.cover)} />              // flat
 *   <div style={coverGradientBackground(article.cover)} />      // hero-style overlay
 * }</pre>
 */

interface BackgroundProps {
  background: string;
  backgroundSize?: string;
  backgroundPosition?: string;
  backgroundRepeat?: string;
}

function isHexColor(s: string): boolean {
  // Conservative check: starts with '#'. We don't validate the chars
  // themselves — anything starting with '#' is treated as a color
  // attempt; CSS will silently render as transparent if the chars
  // are bad, which mirrors the original behavior.
  return s.startsWith('#');
}

function isUrl(s: string): boolean {
  return /^(https?:\/\/|\/\/|\/|data:)/i.test(s);
}

/**
 * Flat cover background — rendered as {@code background-image: url(...)}
 * with {@code background-size: cover} for URLs, as a flat color for
 * hex, or as a neutral fallback for {@code undefined}.
 */
export function coverBackground(cover: string | undefined | null): BackgroundProps {
  if (!cover) {
    return { background: 'var(--color-neutral-200, #e4e4e7)' };
  }
  if (isHexColor(cover)) {
    return { background: cover };
  }
  if (isUrl(cover)) {
    return { background: `url(${cover}) center/cover no-repeat` };
  }
  // Unknown shape — treat as a CSS value (could be a gradient string,
  // a CSS variable, etc.). Matches the legacy behavior for tenants
  // that wrote custom values.
  return { background: cover };
}

/**
 * Gradient-overlay background, used by hero variants where the cover
 * is darkened/styled rather than shown raw. For URLs we layer a
 * gradient over the image; for hex we generate a same-color gradient.
 */
export function coverGradientBackground(cover: string | undefined | null): BackgroundProps {
  if (!cover) {
    return {
      background:
        'linear-gradient(135deg, var(--color-brand-500, #4f46e5), var(--color-brand-700, #312e81))',
    };
  }
  if (isHexColor(cover)) {
    return { background: `linear-gradient(135deg, ${cover}, ${cover}aa)` };
  }
  if (isUrl(cover)) {
    // The gradient sits ABOVE the image because CSS layers right-to-left.
    return {
      background: `linear-gradient(135deg, rgba(0,0,0,0.15), rgba(0,0,0,0.45)), url(${cover}) center/cover no-repeat`,
    };
  }
  return { background: cover };
}

/**
 * Themed custom gradient overlay. Several theme variants
 * (btk, gobito) build cover backgrounds with custom multi-stop
 * gradients tuned to the theme — gradient stop alphas and angles are
 * theme-specific, so a one-size-fits-all {@link coverGradientBackground}
 * would lose visual fidelity.
 *
 * <p>Pass in a {@code gradientFromHex} function — given a hex color
 * seed, return the full CSS gradient string. The helper:
 * <ul>
 *   <li>hex cover → uses the hex as the seed</li>
 *   <li>URL cover → uses {@code fallbackHex} as the seed AND layers
 *       the image behind the gradient</li>
 *   <li>undefined → uses {@code fallbackHex} as the seed (no image
 *       to layer)</li>
 * </ul>
 *
 * <pre>{@code
 *   ...coverWithThemedOverlay(
 *     article.cover,
 *     (seed) => `linear-gradient(180deg, ${seed}33, ${tokens.background})`,
 *     '#3a0e10',
 *   )
 * }</pre>
 */
export function coverWithThemedOverlay(
  cover: string | undefined | null,
  gradientFromHex: (seed: string) => string,
  fallbackHex = '#1a0a0a',
): BackgroundProps {
  if (!cover) {
    return { background: gradientFromHex(fallbackHex) };
  }
  if (isHexColor(cover)) {
    return { background: gradientFromHex(cover) };
  }
  if (isUrl(cover)) {
    // CSS layers comma-separated backgrounds top-down, so the gradient
    // sits ABOVE the image and tints it with the theme's alpha stops.
    return {
      background: `${gradientFromHex(fallbackHex)}, url(${cover}) center/cover no-repeat`,
    };
  }
  return { background: cover };
}

/**
 * Pick a hex seed safely. For overlay-only sites (a
 * {@code position: absolute} corner-tint sitting on top of an outer
 * element that already shows the cover image), we don't want to
 * layer the image again — we just need the hex value to interpolate
 * into the gradient string. Returns the cover when it's a valid hex,
 * the fallback otherwise. Use this for the OVERLAY element when the
 * OUTER already uses {@link coverWithThemedOverlay} to render the
 * image.
 */
export function coverHexSeed(cover: string | undefined | null, fallbackHex = '#1a0a0a'): string {
  if (cover && isHexColor(cover)) return cover;
  return fallbackHex;
}
